import type { PhotoSection } from '../shared/photo';

export type SavedPhotoDraft = {
  file: File;
  caption: string;
  contributorName: string;
  section: PhotoSection;
  key: string;
};
const DRAFT_KEY = 'lloyds-photo-draft';
const PICKER_KEY = 'lloyds-photo-picker';
const MAX_AGE = 24 * 60 * 60 * 1000;

function sessionGet(key: string) {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}
export function pendingPhotoSection(): PhotoSection | null {
  try {
    const pending = JSON.parse(sessionGet(PICKER_KEY) || 'null');
    if (pending && Date.now() - pending.at < MAX_AGE && ['celebration', 'memories'].includes(pending.section))
      return pending.section;
  } catch {
    /* Unavailable or expired recovery state must not stop camera use. */
  }
  return null;
}
export function rememberPicker(section: PhotoSection | null) {
  try {
    if (section) sessionStorage.setItem(PICKER_KEY, JSON.stringify({ section, at: Date.now() }));
    else sessionStorage.removeItem(PICKER_KEY);
  } catch {
    /* Native picking still works when browser storage is blocked. */
  }
}
export function hasPhotoRecovery() {
  return Boolean(pendingPhotoSection() || sessionGet(DRAFT_KEY));
}

let database: Promise<IDBDatabase> | undefined;
export function openDatabase() {
  database ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open('lloyds-photo-recovery', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('drafts', { keyPath: 'key' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('Photo recovery storage is blocked.'));
  });
  return database;
}

const fileBytes = new WeakMap<File, Promise<ArrayBuffer>>();
let writes: Promise<void> = Promise.resolve();
export async function savePhotoDraft(draft: SavedPhotoDraft | null) {
  const previous = sessionGet(DRAFT_KEY);
  // Clear the tab's pointer synchronously on discard/publish so a reload cannot
  // restore a discarded draft even if IndexedDB deletion hasn't finished yet.
  if (draft) sessionStorage.setItem(DRAFT_KEY, draft.key);
  else sessionStorage.removeItem(DRAFT_KEY);
  if (!draft && !previous) return;
  // Serialize edits/discard around the asynchronous byte copy. Store plain
  // bytes: file-backed Blob cloning is not reliable in every WebKit build.
  const operation = writes
    .catch(() => {})
    .then(async () => {
      let bytes: ArrayBuffer | undefined;
      if (draft) {
        if (!fileBytes.has(draft.file)) fileBytes.set(draft.file, draft.file.arrayBuffer());
        bytes = await fileBytes.get(draft.file)!;
      }
      const db = await openDatabase();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction('drafts', 'readwrite');
        const store = tx.objectStore('drafts');
        if (previous && previous !== draft?.key) store.delete(previous);
        if (draft)
          store.put({
            bytes,
            name: draft.file.name,
            type: draft.file.type,
            lastModified: draft.file.lastModified,
            caption: draft.caption,
            contributorName: draft.contributorName,
            section: draft.section,
            key: draft.key,
            savedAt: Date.now(),
          });
        const cursor = store.openCursor();
        cursor.onsuccess = () => {
          const item = cursor.result;
          if (!item) return;
          if (Date.now() - item.value.savedAt >= MAX_AGE) item.delete();
          item.continue();
        };
        tx.oncomplete = () => resolve();
        tx.onabort = tx.onerror = () => reject(tx.error);
      });
    });
  writes = operation;
  await operation;
}

export async function restorePhotoDraft(): Promise<SavedPhotoDraft | null> {
  const key = sessionGet(DRAFT_KEY);
  if (!key) return null;
  const db = await openDatabase();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('drafts', 'readwrite');
    const store = tx.objectStore('drafts');
    const request = store.get(key);
    let result: SavedPhotoDraft | null = null;
    request.onsuccess = () => {
      const saved = request.result;
      if (
        saved &&
        Date.now() - saved.savedAt < MAX_AGE &&
        saved.bytes instanceof ArrayBuffer &&
        saved.bytes.byteLength > 0 &&
        ['celebration', 'memories'].includes(saved.section)
      ) {
        result = {
          file: new File([saved.bytes], saved.name || 'photo.jpg', {
            type: saved.type,
            lastModified: saved.lastModified || saved.savedAt,
          }),
          caption: saved.caption || '',
          contributorName: saved.contributorName || '',
          section: saved.section,
          key,
        };
      } else {
        store.delete(key);
        if (sessionGet(DRAFT_KEY) === key) sessionStorage.removeItem(DRAFT_KEY);
      }
    };
    // Remove abandoned records from earlier tabs after a day.
    const cursor = store.openCursor();
    cursor.onsuccess = () => {
      const item = cursor.result;
      if (!item) return;
      if (Date.now() - item.value.savedAt >= MAX_AGE) item.delete();
      item.continue();
    };
    tx.oncomplete = () => resolve(result);
    tx.onabort = tx.onerror = () => reject(tx.error);
  });
}
